// Variant C — "Immersive Hero with Floating Panel"
// Full-bleed ambient scene · editorial hero text left · auth panel right.

import NumberFlow from "@number-flow/react";
import { AnimatePresence, motion } from "motion/react";
import React, { useEffect, useState } from "react";
import AuthSkeleton from "../components/SkeletonLoaders/AuthSkeleton";
import { AuthPanel, HUE, MeshBackground, useAuthPage, useGlobalStyles } from "./AuthShared";
import { useTheme } from "../providers/ThemeProvider";
import ThemeToggle from "../components/ThemeToggle";

// ─── Orbital floating particles ─────────────────────────────────────────────────
function Orbitals() {
  const dots = Array.from({ length: 24 });
  return (
    <div className="absolute inset-0 pointer-events-none opacity-70 z-[1]">
      {dots.map((_, i) => {
        const sz = 3 + (i % 3);
        const top = 10 + ((i * 71) % 80);
        const left = 10 + ((i * 137) % 80);
        const dur = 3 + (i % 4) * 1.5;
        const hh = (HUE + i * 20) % 360;
        return (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              top: `${top}%`,
              left: `${left}%`,
              width: sz,
              height: sz,
              background: `oklch(0.70 0.10 ${hh})`,
              filter: "blur(0.4px)",
              boxShadow: `0 0 12px oklch(0.70 0.14 ${hh} / 0.6)`,
              animation: `arb-drift ${dur}s ease-in-out ${i * 0.4}s infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

// ─── Live ticker stat ───────────────────────────────────────────────────────────
function TickerStat({ label, value, ticker = false }: { label: string; value: string; ticker?: boolean }) {
  const [count, setCount] = useState(12480);
  useEffect(() => {
    if (!ticker) return;
    const id = setInterval(() => setCount((n) => n + Math.floor(Math.random() * 3)), 5200);
    return () => clearInterval(id);
  }, [ticker]);
  return (
    <div className="flex flex-col gap-[3px]">
      <div className="text-[10.5px] text-gray-400 dark:text-neutral-500 uppercase tracking-[0.6px] font-[550]">{label}</div>
      <div className="text-[18px] font-semibold text-gray-900 dark:text-gray-100 tracking-[-0.4px]">
        {ticker
          ? <NumberFlow value={count} format={{ useGrouping: true }} locales="en-US" />
          : value}
      </div>
    </div>
  );
}

// ─── Avatar stack ───────────────────────────────────────────────────────────────
// function AvatarStack() {
//   return (
//     <div className="inline-flex">
//       {[0, 1, 2, 3].map((i) => (
//         <div
//           key={i}
//           className="w-[22px] h-[22px] rounded-full border-2 border-white/85"
//           style={{
//             background: `linear-gradient(135deg, oklch(0.80 0.10 ${(HUE + i * 60) % 360}), oklch(0.60 0.12 ${(HUE + i * 60 + 30) % 360}))`,
//             marginLeft: i === 0 ? 0 : -7,
//           }}
//         />
//       ))}
//     </div>
//   );
// }

// ─── Typewriter hero ────────────────────────────────────────────────────────────
const SLIDES = [
  {
    text: "Track Your App Performance",
    desc: "Every install, session, and revenue event in one unified timeline — real-time.",
  },
  {
    text: "Optimize Ad Campaigns",
    desc: "Cut wasted spend with AI-powered attribution signals across Google Ads and Apple Search Ads.",
  },
  {
    text: "Grow Your Revenue",
    desc: "Surface the highest-ROI opportunities at country level and act on them instantly.",
  },
];

const TYPE_SPEED = 48;
const DELETE_SPEED = 28;
const HOLD_MS = 2200;
const PAUSE_MS = 400;

function TypewriterHero() {
  const { isDark } = useTheme();
  const [slideIdx, setSlideIdx] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [phase, setPhase] = useState<"typing" | "holding" | "deleting" | "pausing">("typing");
  const [descIdx, setDescIdx] = useState(0);

  useEffect(() => {
    const target = SLIDES[slideIdx].text;

    if (phase === "typing") {
      if (displayed.length < target.length) {
        const t = setTimeout(() => setDisplayed(target.slice(0, displayed.length + 1)), TYPE_SPEED);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => setPhase("holding"), HOLD_MS);
        return () => clearTimeout(t);
      }
    }

    if (phase === "holding") {
      setPhase("deleting");
    }

    if (phase === "deleting") {
      if (displayed.length > 0) {
        const t = setTimeout(() => setDisplayed((d) => d.slice(0, -1)), DELETE_SPEED);
        return () => clearTimeout(t);
      } else {
        setPhase("pausing");
      }
    }

    if (phase === "pausing") {
      const next = (slideIdx + 1) % SLIDES.length;
      const t = setTimeout(() => {
        setSlideIdx(next);
        setDescIdx(next);
        setPhase("typing");
      }, PAUSE_MS);
      return () => clearTimeout(t);
    }
  }, [phase, displayed, slideIdx]);

  return (
    <div style={{ width: "100%", maxWidth: "clamp(500px, 42vw, 750px)" }}>
      {/* Fixed prefix */}
      <p className="mb-2 font-semibold text-gray-800 dark:text-gray-200" style={{ fontSize: "clamp(54px, 1.1vw, 58px)" }}>
        You can
      </p>

      {/* Typewriter line */}
      <h1
        className="mb-0 leading-[1.1] font-bold text-gray-900 dark:text-gray-100 tracking-[-0.04em]"
        style={{ fontSize: "clamp(32px, 3.2vw, 52px)" }}
      >
        <span
          className="arb-gradient-text"
          style={{
            backgroundImage: isDark
              ? `linear-gradient(100deg, #e2e8f0 0%, oklch(0.72 0.18 ${HUE}) 50%, #93c5fd 100%)`
              : `linear-gradient(100deg, #1a3d7c 0%, oklch(0.45 0.16 ${HUE}) 50%, #0ea5e9 100%)`,
          }}
        >
          {displayed}
        </span>
        {/* blinking cursor */}
        <span
          className="inline-block w-[3px] ml-[2px] rounded-sm bg-primary-500 align-middle"
          style={{ height: "0.85em", animation: "arb-blink 1s step-end infinite" }}
        />
      </h1>

      {/* Fading description */}
      <div className="mt-5 min-h-[3.5em]">
        <AnimatePresence mode="wait">
          <motion.p
            key={descIdx}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="leading-[1.65] text-gray-600 dark:text-neutral-400 m-0"
            style={{ fontSize: "clamp(13px, 1.05vw, 15px)", maxWidth: "clamp(260px, 30vw, 420px)" }}
          >
            {SLIDES[descIdx].desc}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Dot indicators */}
      <div className="mt-6 flex gap-2 items-center">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => { setSlideIdx(i); setDescIdx(i); setDisplayed(""); setPhase("typing"); }}
            className="rounded-full transition-all duration-300 border-none cursor-pointer p-0"
            style={{
              width: i === slideIdx ? 20 : 6,
              height: 6,
              background: i === slideIdx
                ? (isDark ? "#60a5fa" : "#1a3d7c")
                : (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.18)"),
            }}
          />
        ))}
      </div>

      {/* Stats ticker */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="mt-8 inline-flex flex-wrap gap-y-3 p-3 xl:p-[14px_20px] rounded-xl bg-white/55 dark:bg-neutral-800/80 border border-black/[0.09] dark:border-neutral-700 backdrop-blur-[20px]"
      >
        <TickerStat label="Revenue tracked" value="$4.2B+" />
        <div className="w-px bg-black/[0.09] dark:bg-white/[0.12] mx-[22px]" />
        <TickerStat label="ROAS lift (avg)" value="+34%" />
        <div className="w-px bg-black/[0.09] dark:bg-white/[0.12] mx-[22px]" />
        <TickerStat label="Apps onboarded" value="12,480" ticker />
      </motion.div>
    </div>
  );
}

// ─── Variant C page ─────────────────────────────────────────────────────────────
const AuthVariantC: React.FC = () => {
  useGlobalStyles();
  const { isValidating, referralCode, showSignUpTab, handleSwapToLogin, navigateTo, redirectIfAuthenticated } = useAuthPage();

  if (redirectIfAuthenticated) return redirectIfAuthenticated;

  return (
    <div className="fixed inset-0 overflow-hidden flex flex-col text-gray-900 dark:text-gray-100 dark:bg-neutral-950 [font-family:var(--font-geist)]">
      <MeshBackground />
      <Orbitals />

      {/* Theme toggle — top-right corner */}
      <div className="absolute top-4 right-4 z-[10]">
        <ThemeToggle />
      </div>

      {/* Main — flex-1, fills entire screen height */}
      <div className="relative flex-1 flex flex-col justify-between lg:flex-row overflow-hidden z-[2] max-w-[1400px] w-full mx-auto">

        {/* Left — editorial hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:flex items-center overflow-hidden pl-10 xl:pl-14 2xl:pl-20 pr-6"
        >
          <TypewriterHero />
        </motion.div>

        {/* Right — auth panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col justify-center items-center flex-shrink-0 w-full lg:w-[600px] overflow-hidden px-4 sm:px-8 lg:px-8 xl:px-10 z-[3]"
        >
          <div className="w-full" style={{ maxWidth: "clamp(360px, 90vw, 540px)" }}>
            {/* Glass card */}
            <div className="relative overflow-hidden p-[24px_24px_22px] sm:p-[28px_28px_26px] bg-white/[0.88] dark:bg-neutral-900/95 backdrop-blur-[28px] saturate-[160%] border border-black/[0.09] dark:border-neutral-700 rounded-2xl shadow-xl">
              {/* Top gradient accent bar */}
              <div
                className="absolute top-0 left-0 right-0 h-[2px]"
                style={{
                  background: "linear-gradient(90deg, transparent, #1a3d7c, transparent)",
                  backgroundSize: "200% 100%",
                  animation: "arb-glow-trail 4s linear infinite",
                }}
              />

              {isValidating ? <AuthSkeleton /> : (
                <AuthPanel
                  referralCode={referralCode}
                  showSignUpTab={showSignUpTab}
                  onSwapToLogin={handleSwapToLogin}
                  navigateTo={navigateTo}
                />
              )}
            </div>

            {/* Trust chip below card */}
            {/* <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="mt-3 flex gap-2.5 items-center px-4 py-[9px] rounded-full bg-white/65 border border-black/[0.09] backdrop-blur-[18px] text-[12px] text-gray-400 mx-auto w-fit"
            >
              <AvatarStack />
              <span><span className="text-gray-900 font-semibold">12,480</span> teams already growing with us</span>
            </motion.div> */}
          </div>
        </motion.div>
      </div>

      {/* Bottom status bar */}
      <div className="relative flex-shrink-0 z-[4] flex flex-wrap justify-center gap-x-3 gap-y-1 py-3 text-[11px] sm:text-[11.5px] text-gray-400 [font-family:var(--font-mono)] tracking-[0.3px]">
        <a href="https://apprevbooster.com/privacy-policy" target="_blank" rel="noreferrer" className="text-inherit no-underline hover:text-gray-600 transition-colors">Privacy</a>
        <span>·</span>
        <a href="https://apprevbooster.com/terms-of-service" target="_blank" rel="noreferrer" className="text-inherit no-underline hover:text-gray-600 transition-colors">Terms</a>
      </div>
    </div>
  );
};

export default AuthVariantC;
